import { Dispatch } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { t } from 'i18next';
import { setError } from 'store/states/ErrorSlice';
import { IErrorApiProps, IErrorMessage } from 'types/index';
import type { IApiError, IApiErrorResponse } from '../../types/IApiTypes';
import {
  EApiType,
  ECommonDialogType,
  EDisplayType,
  EMessageType,
  errorConfig,
  GlobalHttpErrorMessageKey,
} from '../data/Constants';
import { openDialog } from '../store/states/DialogSlice';
import { showToastMessage } from './ToastHelper';

/**
 * Unified API error handler (config-driven).
 * - Stores the error in Redux state.
 * - Resolves feedback config from: inline config > action lookup > global fallback.
 *
 * Message resolution order:
 *  1. errorId param → t('messages.error.{errorId}')
 *  2. config.messages[statusCode] → t(that key)
 *  3. config.messages.default → t(that key)
 *  4. Global fallback → t('errorMessages.http.{statusCode}')
 *
 * @param err - The caught error (typically an Axios error with a response property)
 * @param action - EApiType (looks up errorConfig) or inline IErrorApiProps
 * @returns {{ error: IApiError['response'] | undefined }}
 */
export function handleError(err: unknown, action?: EApiType | IErrorApiProps, dispatch?: Dispatch) {
  const error = (err as IApiError)?.response;
  const statusCode = error?.status;

  // Always store error in Redux state
  dispatch?.(setError(error));

  // Resolve the error config
  const config = getErrorConfig(action, statusCode);

  if (!config) {
    return { error };
  }

  const message = getMessage(config, statusCode, error);

  // Resolve messageType (status-specific override or default)
  const messageData: IErrorMessage =
    (statusCode != null && config.messages?.[statusCode]) || config.messages?.default;

  // Show feedback
  if (messageData) {
    showMessage(config, message, messageData, dispatch);
  }

  return { error };
}

/**
 * Resolves the error config from action type, inline config, or global fallback.
 */
function getErrorConfig(
  action?: EApiType | IErrorApiProps,
  statusCode?: number
): IErrorApiProps | undefined {
  if (!action) {
    // No action provided → use global HTTP fallback as dialog
    return {
      displayAs: EDisplayType.Dialog,
      dialogType: ECommonDialogType.Error,
      messages: { default: { message: `${GlobalHttpErrorMessageKey}.${statusCode ?? 500}` } },
    };
  }
  if (typeof action === 'object') {
    return action;
  }
  return errorConfig[action];
}

/**
 * Resolves the display message using priority:
 *  1. errorId → t('messages.error.{errorId}')
 *  2. config.messages[statusCode]
 *  3. config.messages.default
 *  4. Server message → error.response.data.message (direct from API)
 *  5. Global fallback → t('errorMessages.http.{statusCode}')
 */
function getMessage(
  config: IErrorApiProps,
  statusCode?: number,
  error?: IApiErrorResponse
): string {
  // Resolve message with priority chain (auto-extract errorId from response if not passed)
  const errorId = error?.data?.errorId;
  const serverMessage = error?.data?.message;
  if (errorId) {
    const msg = t(`messages.error.${errorId}`, getMessagePlaceholder(config, serverMessage));
    if (msg) return msg;
  }
  const messageDetails = config.messages;
  // Check status-specific message in config
  if (statusCode && messageDetails[statusCode]) {
    const msg = t(messageDetails[statusCode].message, '');
    if (msg) return msg;
  }

  // Use config default
  const defaultMsg = t(messageDetails.default.message, '');
  if (defaultMsg) return defaultMsg;

  // Use server-provided message directly from API response
  if (serverMessage) return serverMessage;

  // Ultimate global fallback
  return t(`${GlobalHttpErrorMessageKey}.${statusCode ?? 500}`, 'An error occurred');
}

/**
 * Builds i18next interpolation options for the configured message placeholder.
 *
 * @param config - Error feedback configuration containing the optional placeholder name.
 * @param serverMessage - API-provided message used as the placeholder value.
 * @returns Interpolation options for i18next, or an empty string when no placeholder should be applied.
 */
function getMessagePlaceholder(
  config: IErrorApiProps,
  serverMessage?: string
): Record<string, string> | string {
  if (!config.messagePlaceholder || !serverMessage) {
    return '';
  }

  return { [config.messagePlaceholder]: serverMessage };
}

/**
 * Dispatches feedback (dialog or toast) based on the resolved config.
 */
function showMessage(
  config: IErrorApiProps,
  message: string,
  messageData: IErrorMessage,
  dispatch?: Dispatch
): void {
  switch (config.displayAs) {
    case EDisplayType.Dialog:
      dispatch?.(
        openDialog({
          type: ECommonDialogType.Error,
          data: message,
        })
      );
      break;
    case EDisplayType.Toast:
      showToastMessage(
        messageData.type as EMessageType,
        message,
        false,
        true,
        messageData.toastPosition
      );
      break;
    case EDisplayType.None:
      break;
  }
}

export const successHandle = (resSuccess: AxiosResponse, dispatch?: Dispatch): void => {
  let dialogData = {
    show: true,
    type: ECommonDialogType.Success,
    data: t('messages.category.savedSuccessfully'),
  };
  if (resSuccess) {
    dispatch?.(openDialog(dialogData));
  } else {
    dialogData = { ...dialogData, type: ECommonDialogType.Error };
    dispatch?.(openDialog(dialogData));
  }
};
